import { useQuery } from '@tanstack/react-query'
import { formsApi } from '@/api/formsApi'
import { productsApi } from '@/api/productsApi'
import { storageApi } from '@/api/storageApi'
import { equipmentApi } from '@/api/equipmentApi'
import { useTenant } from '@/providers/TenantProvider'
import { storageEntryToAuditRow, type TrailSource } from '@/lib/auditTrailSources'
import type { AuditTrailRow } from '@/types/audit'

/** The storage trail is paged; the pop-up shows its first, largest page. */
const STORAGE_PAGE = 200

export interface TrailRows {
  rows: AuditTrailRow[]
  /** Set when the trail holds more rows than were loaded. */
  total?: number
}

/**
 * The rows of whichever trail the audit pop-up is showing (z8nrz7dq6q), all in
 * the one `AuditTrailRow` shape so one table renders every kind.
 *
 * Never cached across opens: an audit trail is read to find out what happened,
 * and a stale one is the one thing it must not be.
 */
export function useAuditTrailSource(source: TrailSource | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['audit-trail-popup', tenantId, source?.kind, source?.id],
    enabled: !!source && !!tenantId,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<TrailRows> => {
      const s = source as TrailSource
      const tenant = tenantId as string
      switch (s.kind) {
        case 'patient':
          return { rows: await formsApi.getPatientAuditTrail(s.id) }
        case 'submission':
          return { rows: await formsApi.getAuditTrail(s.id) }
        case 'product':
          return { rows: await productsApi.listProductAudit(tenant, s.id) }
        case 'equipment':
          return { rows: await equipmentApi.getAuditTrail(tenant, s.id) }
        case 'lot': {
          // Everywhere the lot has been stored, across positions.
          const page = await storageApi.lotAudit(tenant, s.id, { take: STORAGE_PAGE })
          return {
            rows: page.items.map(e => storageEntryToAuditRow(e)),
            total: page.total > page.items.length ? page.total : undefined,
          }
        }
        case 'storage': {
          // `subtree`: a unit or rack has no events of its own; its positions do.
          const page = await storageApi.nodeAudit(tenant, s.id, { take: STORAGE_PAGE, subtree: true })
          return {
            rows: page.items.map(e => storageEntryToAuditRow(e, s.label)),
            total: page.total > page.items.length ? page.total : undefined,
          }
        }
      }
    },
  })
}
